import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded'
import { alpha, Box, Typography, useTheme } from '@mui/material'

import { GAME_ICON_RADIUS, gameIconSx } from '../../helpers/styles'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { GameMetadata, GenreCreativeAggregate } from '../../types'

/** Comparison target rendered on the right side of the matchup. */
export type CompareMatchupTarget = { kind: 'game'; game: GameMetadata } | { kind: 'genre'; aggregate: GenreCreativeAggregate }

interface CompareMatchupIdentityProps {
  /** Source game (left side of the matchup). */
  source: GameMetadata
  target: CompareMatchupTarget
  /** Collapsed (scrolled) state — shrinks icons/text and hides the subtitle. */
  compact: boolean
}

/**
 * Side-by-side "source vs target" identity for the Compare page sticky header.
 * Mirrors the single-game identity styling of the UA Insights game page header
 * (squircle icon + bold name + caption subtitle, shrinking when the header
 * collapses) so the Compare page reads as a continuation of the game page it
 * was opened from. Each side ellipsises long names so the matchup never pushes
 * the sticky header taller or shoves the right-side selectors off screen.
 */
export const CompareMatchupIdentity: FC<CompareMatchupIdentityProps> = ({ source, target, compact }) => {
  const { t } = useTranslation('ua-insights')
  const { genreLabel } = useUATaxonomy()

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 1.5, flex: 1, minWidth: 0 }}>
      <Identity icon={source.icon} name={source.name} subtitle={genreLabel(source.genre)} compact={compact} />
      <Typography
        component="span"
        sx={{
          flexShrink: 0,
          fontWeight: 'bold',
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontSize: compact ? 12 : 14,
        }}
      >
        {t('compare_vs')}
      </Typography>
      {target.kind === 'game' ? (
        <Identity icon={target.game.icon} name={target.game.name} subtitle={genreLabel(target.game.genre)} compact={compact} />
      ) : (
        <Identity
          iconSlot={<GenreIconBadge compact={compact} />}
          name={genreLabel(target.aggregate.genre)}
          subtitle={t('compare_picker_genre_games_count', { count: target.aggregate.contributingGamesCount })}
          compact={compact}
        />
      )}
    </Box>
  )
}

interface IdentityProps {
  /** Image URL for the squircle icon. Ignored when `iconSlot` is provided. */
  icon?: string | null
  /** Custom icon element (used for the generic genre badge). */
  iconSlot?: ReactNode
  name: string
  subtitle?: string | null
  compact: boolean
}

const Identity: FC<IdentityProps> = ({ icon, iconSlot, name, subtitle, compact }) => {
  const size = compact ? 26 : 38
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 1.25, minWidth: 0, flex: '0 1 auto', maxWidth: 280 }}>
      {iconSlot ??
        (icon ? (
          <Box
            component="img"
            src={icon}
            alt={name}
            sx={{ ...gameIconSx(size), boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.15)', transition: 'width 160ms ease, height 160ms ease' }}
          />
        ) : null)}
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            fontSize: compact ? 15 : 18,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </Typography>
        {!compact && subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: 12, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

/**
 * Generic category badge standing in for a genre's (absent) icon in the same
 * squircle slot a game icon would occupy, keeping the matchup row balanced.
 */
const GenreIconBadge: FC<{ compact: boolean }> = ({ compact }) => {
  const theme = useTheme()
  const size = compact ? 26 : 38
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: GAME_ICON_RADIUS,
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'width 160ms ease, height 160ms ease',
      }}
    >
      <CategoryRoundedIcon sx={{ fontSize: compact ? 16 : 22 }} />
    </Box>
  )
}
